import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { Link } from 'generated/bslOmniShellNav/models';
import { SharedObjectsData } from 'generated/sharedObjects/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { GetUserInfoSuccessType } from 'models/AuthTypes';
import { InternalUser, UserOrganizationUnit, UserRole } from 'models/context/InternalUser';
import { UserInfo } from 'models/UserInfo';
import { setLoadingAction } from 'saga/acl/ducks';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { isAppInitialized } from 'saga/appHandlers/ducks';
import { AuthActionTypes, getUserInfoActions, logoutActions, selectIsUserInternal } from 'saga/auth/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';
import { api as sharedObjectsApi } from 'saga/sharedObjects/api';
import { selectSharedObjectsData } from 'saga/sharedObjects/ducks';

/* STATE */
export interface ContextInternalState {
  user: InternalUser | null;
  userList: InternalUser[];
  navigationLinks: Link[];
}

/* ACTION TYPES */
export enum ContextInternalActionTypes {
  SetContextInternalUser = '@@Context/Internal/SET_CONTEXT_INTERNAL_USER',
  SetContextScreenFilter = '@@Context/Internal/SET_CONTEXT_SCREEN_FILTER',
  SetPrimaryOU = '@@Context/Internal/SET_PRIMARY_OU',
  SetSelectedRole = '@@Context/Internal/SET_SELECTED_ROLE',
  GetInternalUserList = '@@Context/Internal/GET_INTERNAL_USER_LIST',
  ClearInternalUserList = '@@Context/Internal/CLEAR_INTERNAL_USER_LIST',
  GetNavigationLinks = '@@Context/Internal/GET_NAVIGATION_LINKS',
}

/* ACTIONS */
export const setContextInternalUserActions = createApiActionCreators(ContextInternalActionTypes.SetContextInternalUser);
export const setExternalScreenFilterAction = createApiActionCreators(ContextInternalActionTypes.SetContextScreenFilter);
export const setPrimaryOUActions = createApiActionCreators(ContextInternalActionTypes.SetPrimaryOU);
export const setSelectedRoleActions = createApiActionCreators(ContextInternalActionTypes.SetSelectedRole);
export const getInternalUserListActions = createApiActionCreators(ContextInternalActionTypes.GetInternalUserList);
export const clearInternalUserListAction = createActionCreator(ContextInternalActionTypes.ClearInternalUserList);
export const getNavigationLinksAction = createApiActionCreators(ContextInternalActionTypes.GetNavigationLinks);

/* REDUCERS */
const initialState: ContextInternalState = {
  user: null,
  userList: [],
  navigationLinks: [],
};

const user = createReducer(initialState.user, {
  [ContextInternalActionTypes.SetContextInternalUser]: {
    [RequestActionTypes.SUCCESS]: (_state: InternalUser | null, payload: InternalUser) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ContextInternalActionTypes.SetContextScreenFilter]: {
    [RequestActionTypes.SUCCESS]: (state: InternalUser, payload: boolean) => ({
      ...state,
      ...{ screenFilter: payload ? 'external' : 'internal' },
    }),
  },
  [ContextInternalActionTypes.SetPrimaryOU]: {
    [RequestActionTypes.SUCCESS]: (_state: InternalUser | null, id: string) => {
      if (_state) {
        _state.orgUnits?.forEach((primaryOu) => {
          primaryOu.isPrimary = primaryOu.ouRefNo === id;
        });
      }

      return _state;
    },
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => null,
  },
});

const userList = createReducer(initialState.userList, {
  [ContextInternalActionTypes.ClearInternalUserList]: () => initialState.userList,
  [ContextInternalActionTypes.GetInternalUserList]: {
    [RequestActionTypes.SUCCESS]: (state: InternalUser[] | null, payload: InternalUser[]) => {
      return payload;
    },
    [RequestActionTypes.FAILURE]: () => initialState.userList,
  },
});

const navigationLinks = createReducer(initialState.navigationLinks, {
  [ContextInternalActionTypes.GetNavigationLinks]: {
    [RequestActionTypes.SUCCESS]: (_state: Link[] | null, payload: Link[]) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

export default combineReducers<ContextInternalState>({
  user,
  userList,
  navigationLinks,
});

/* SELECTORS */
const selectContextInternal = (state: AppState) => state.context.internal;

export const selectContextInternalUserList = (state: AppState): InternalUser[] => selectContextInternal(state).userList;

export const selectContextInternalUser = (state: AppState): InternalUser | null =>
  selectIsUserInternal(state) ? selectContextInternal(state).user : null;

export const selectContextInternalUserName = (state: AppState): string => {
  if (selectIsUserInternal(state)) {
    return selectContextInternalUser(state)?.displayName || '';
  }

  return '';
};

export const selectContextInternalOrganizationUnits = (state: AppState): UserOrganizationUnit[] => {
  if (selectIsUserInternal(state)) {
    return selectContextInternalUser(state)?.orgUnits || [];
  }

  return [];
};

export const selectContextInternalActiveOrganizationUnit = (state: AppState): UserOrganizationUnit | undefined => {
  if (selectIsUserInternal(state)) {
    return selectContextInternalOrganizationUnits(state).find((unit) => unit.isPrimary);
  }

  return undefined;
};

export const selectContextInternalRoles = (state: AppState): UserRole[] => {
  if (selectIsUserInternal(state)) {
    return selectContextInternalUser(state)?.roles?.filter((role) => role.isExclusive) || [];
  }

  return [];
};

export const selectContextInternalSelectedRole = (state: AppState): UserRole | undefined => {
  if (selectIsUserInternal(state)) {
    return selectContextInternalRoles(state).find((role) => role.isPrimary);
  }

  return undefined;
};

export const selectContextInternalIsUserScreenFilterDisabled = (state: AppState): boolean => {
  if (selectIsUserInternal(state)) {
    return (
      selectContextInternalUser(state)?.screenFilter === undefined ||
      selectContextInternalUser(state)?.screenFilter === 'internal'
    );
  }

  return false;
};

export const selectNavigationLinks = (state: AppState): Link[] => selectContextInternal(state).navigationLinks;

/* SAGAS */
function* setContextInternalUser({ payload: login }: any) {
  const appInitialized: boolean = yield select(isAppInitialized);
  const request: ApiClientRequest = {
    apiCall: call(api.getInternalUser, yield getApiUrl(environment.urlConfig.internalUser), login, !appInitialized),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  // Check if sharedObject has defined externalScreenFilter
  const sharedObject: SharedObjectsData = yield select(selectSharedObjectsData);

  if (resp.ok) {
    yield put(setContextInternalUserActions.success(resp.data));

    if (sharedObject?.internalUser?.preferences?.externalScreenFilter !== undefined) {
      yield put(setExternalScreenFilterAction.success(sharedObject?.internalUser?.preferences?.externalScreenFilter));
    }
    // reload translations with internal param set to true after login
  } else {
    console.log('setContextInternalUser failure: api.getInternalUser failed');
    yield put(setContextInternalUserActions.failure());
  }
}

function* setPrimaryOU({ payload: id }: any) {
  const request: ApiClientRequest = {
    apiCall: call(api.setPrimaryOU, yield getApiUrl(environment.urlConfig.user), id),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(setPrimaryOUActions.success(id));
    yield put(getUserInfoActions.request(true));
  } else {
    console.log('setPrimaryOU failure: api.setPrimaryOU failed');
    yield put(setPrimaryOUActions.failure());
  }
}

function* setSelectedRole({ payload: id }: any) {
  const request: ApiClientRequest = {
    apiCall: call(api.setSelectedRole, yield getApiUrl(environment.urlConfig.userAccess), id),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(logoutActions.request());
  } else {
    console.log('setSelectedRole failure: api.setSelectedRole failed');
    yield put(setSelectedRoleActions.failure());
  }
}

function* getInternalUserList({ payload: searchString }: any) {
  if (!searchString) {
    return;
  }

  const loadPageCount: string = selectProperty('gui.journalingHistory.loadPageCount.internalUserSearch');
  const loadPageSize: string = selectProperty('gui.journalingHistory.loadPageSize.internalUserSearch');
  const minCharsToSearch: string = selectProperty('gui.journalingHistory.minCharsToSearch');

  if (searchString.length >= minCharsToSearch) {
    const request: ApiClientRequest = {
      apiCall: call(
        api.getInternalUserList,
        yield getApiUrl(environment.urlConfig.internalUser),
        searchString,
        loadPageCount,
        loadPageSize
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(getInternalUserListActions.success(resp.data));
    } else {
      console.log('getInternalUserList failure: api.getInternalUserList failed');
      yield put(setSelectedRoleActions.failure());
    }
  }
}

function* setExternalScreenFilter({ payload: screenFilterData }: any) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      sharedObjectsApi.setExternalScreenFilterAction,
      yield getApiUrl(environment.urlConfig.sharedObjects),
      {
        externalScreenFilter: screenFilterData,
      }
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(setExternalScreenFilterAction.success(screenFilterData));
  } else {
    console.log('setExternalScreenFilter failure: api.setExternalScreenFilterAction failed');
    yield put(setExternalScreenFilterAction.failure());
  }
}

function* afterGetUserInfoSuccess({ payload }: AppAction<GetUserInfoSuccessType>) {
  const userInfo: UserInfo = payload.userInfo;
  const forceUpdate: boolean = payload.forceUpdate;
  const internalUser: InternalUser = yield select(selectContextInternalUser);

  if (userInfo?.logged && userInfo?.internal && (forceUpdate || userInfo?.username !== internalUser?.login)) {
    yield put(setContextInternalUserActions.request(userInfo.username));
  }
}

function* getNavigationLinks() {
  const url = environment.properties['bsl.omni.getNavigationLinks.url'];

  const request: ApiClientRequest = {
    apiCall: call(api.getNavigationLinks, url),
    skipDefaultKahlBehavior: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data.links) {
      yield put(getNavigationLinksAction.success(resp.data.links));
    } else {
      yield put(getNavigationLinksAction.failure());
    }
  } else {
    if (resp.status !== 403) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID1720' }));
    }

    yield put(getNavigationLinksAction.failure());
  }
}

/* EXPORT */
export function* contextInternalSaga() {
  yield takeLatest(
    createActionType(ContextInternalActionTypes.SetContextInternalUser, RequestActionTypes.REQUEST),
    setContextInternalUser
  );

  yield takeLatest(createActionType(AuthActionTypes.GetUserInfo, RequestActionTypes.SUCCESS), afterGetUserInfoSuccess);

  yield takeLatest(
    createActionType(ContextInternalActionTypes.SetContextScreenFilter, RequestActionTypes.REQUEST),
    setExternalScreenFilter
  );

  yield takeLatest(createActionType(ContextInternalActionTypes.SetPrimaryOU, RequestActionTypes.REQUEST), setPrimaryOU);

  yield takeLatest(
    createActionType(ContextInternalActionTypes.SetSelectedRole, RequestActionTypes.REQUEST),
    setSelectedRole
  );

  yield takeLatest(
    [
      createActionType(ContextInternalActionTypes.GetInternalUserList, RequestActionTypes.REQUEST),
      ContextInternalActionTypes.ClearInternalUserList,
    ],
    getInternalUserList
  );

  yield takeLatest(
    createActionType(ContextInternalActionTypes.GetNavigationLinks, RequestActionTypes.REQUEST),
    getNavigationLinks
  );
}
